import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Input, Modal, Select, Typography } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import ToggleRow from '../common/ToggleRow';
import UserRef from '../users/UserRef';
import {
	LinkShapePicker,
	LinkShapeSummary,
	filterScopeOption,
	shapeComplete,
	shapeToPayload,
	useLinkVocabulary,
} from './LinkShape';
import {
	LINK_LIMITS,
	approveLinkRequest,
	createLink,
	declineLinkRequest,
	linkShape,
	requestLink,
} from '../../services/collectionLinks';
import {
	COPIES_DESCRIPTION,
	COPIES_LABEL,
	KEYS_DESCRIPTION,
	KEYS_LABEL,
	describeLinkError,
	describeRequesterReach,
	requestExceedsReach,
} from '../../utils/collectionLinks';
import { cardAndDescendantIds } from '../../utils/facets';
import { normalizeId } from '../../utils/ids';
import { nestSubCollections, nestedOptionClass } from '../../utils/collectionFamily';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

const { Text } = Typography;

const WHOLE = { kind: 'whole', cardId: null, query: null };

/** A collection picker whose rows are the collections' own badges. */
function CollectionPicker({ collections, value, onChange, placeholder, ariaLabel }) {
	// Alphabetical, each sub-collection indented beneath its parent.
	const options = useMemo(() => nestSubCollections((collections || [])
		.filter(collection => normalizeId(collection?._id))
		.sort((a, b) => (a?.name || 'Untitled collection').localeCompare(b?.name || 'Untitled collection')))
		.map(({ collection, depth }) => ({
			value: normalizeId(collection._id),
			title: collection.name || 'Untitled collection',
			label: collection.name || 'Untitled collection',
			className: nestedOptionClass(depth),
			collection,
		})), [collections]);
	return (
		<Select
			className="linked-collections__picker"
			aria-label={ariaLabel}
			showSearch
			value={value || undefined}
			onChange={onChange}
			options={options}
			optionRender={option => (
				<RecordBadge kind="collection" record={option.data.collection} title={option.data.title} size="xs" />
			)}
			filterOption={filterScopeOption}
			placeholder={placeholder}
			notFoundContent="No collections to pick from"
		/>
	);
}

function Field({ label, htmlFor, children, hint = null }) {
	return (
		<div className="linked-collections__field">
			{htmlFor
				? <label className="linked-collections__field-label" htmlFor={htmlFor}>{label}</label>
				: <span className="linked-collections__field-label">{label}</span>}
			{children}
			{hint ? <Text type="secondary" className="small">{hint}</Text> : null}
		</div>
	);
}

function Switches({ allowCopies, allowKeys, onCopies, onKeys }) {
	return (
		<div className="linked-collections__switches">
			<ToggleRow first label={COPIES_LABEL} description={COPIES_DESCRIPTION} checked={allowCopies} onChange={onCopies} />
			<ToggleRow label={KEYS_LABEL} description={KEYS_DESCRIPTION} checked={allowKeys} onChange={onKeys} />
		</div>
	);
}

/**
 * "Link a collection…" — A's manager shares this collection with another
 * collection they ALSO manage (a direct link needs both consents; anyone else
 * asks from the other side). `candidates` are those collections; `existing`
 * the outgoing links, so re-stating one says what it replaces.
 */
export function LinkCollectionModal({ open, onClose, onDone, sourceId, candidates = [], existing = [], vocabulary }) {
	const [targetId, setTargetId] = useState(null);
	const [shape, setShape] = useState(WHOLE);
	const [allowCopies, setAllowCopies] = useState(false);
	const [allowKeys, setAllowKeys] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	// An element name the link's filter could mean several of (400
	// `ambiguous_element`, element ids step 3): picked here, sent again.
	const elementPicks = useElementChoices(open ? JSON.stringify(shape) : null);
	const resetElementPicks = elementPicks.reset;

	useEffect(() => {
		if (!open) return;
		setTargetId(null);
		setShape(WHOLE);
		setAllowCopies(false);
		setAllowKeys(true);
		setError(null);
		resetElementPicks();
	}, [open, resetElementPicks]);
	useEffect(() => { resetElementPicks(); }, [shape, resetElementPicks]);

	// POST is a complete statement per (target, card): the same pair again
	// replaces that link's filter and switches rather than adding a second.
	const replaces = targetId && existing.some(link => (
		normalizeId(link?.targetId) === normalizeId(targetId)
		&& (normalizeId(link?.card_id) || null) === (shape.kind === 'card' ? normalizeId(shape.cardId) : null)
	));

	const submit = async () => {
		if (!targetId || !shapeComplete(shape)) return;
		setBusy(true);
		setError(null);
		try {
			const link = await createLink(sourceId, elementPicks.withChoices({ targetId, ...shapeToPayload(shape), allowCopies, allowKeys }));
			onDone?.(link);
		} catch (err) {
			if (!elementPicks.take(err)) setError(describeLinkError(err, 'The link could not be made.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Link a collection"
			okText="Link collection"
			okButtonProps={{ loading: busy, disabled: !targetId || !shapeComplete(shape) }}
			onOk={submit}
			onCancel={onClose}
			destroyOnClose
			className="linked-collections__modal"
		>
			<p className="linked-collections__lead">
				Everyone who can see the whole of the other collection will be able to read what you share, read-only.
			</p>
			<Field label="Share with">
				<CollectionPicker
					ariaLabel="Share with"
					collections={candidates}
					value={targetId}
					onChange={setTargetId}
					placeholder={candidates.length ? 'Pick a collection you manage' : 'You manage no other collection that can take a link'}
				/>
			</Field>
			<Field label="What to share">
				<LinkShapePicker
					value={shape}
					onChange={setShape}
					cardOptions={vocabulary?.cardOptions || []}
					filterFields={vocabulary?.filterFields || []}
					collectionId={sourceId}
				/>
			</Field>
			<Switches allowCopies={allowCopies} allowKeys={allowKeys} onCopies={setAllowCopies} onKeys={setAllowKeys} />
			{replaces ? (
				<Alert type="info" showIcon message="This collection already has that link. Linking again replaces its filter and switches with these." />
			) : null}
			<Text type="secondary" className="small">
				To share with a collection you don't manage, one of its managers asks for the link from their Sharing tab, and you approve it here.
			</Text>
			{error ? <Alert type="error" showIcon role="alert" message={error} /> : null}
			<QueryRefusalPick inline refusal={elementPicks.refusal} disabled={busy} onPick={(value) => { elementPicks.pick(value); submit(); }} onRepick={() => { elementPicks.clearRefusal(); submit(); }} access={shape.kind === 'query' ? (shape.query || '') : ''} />
		</Modal>
	);
}

/**
 * "Request a link…" — B's manager asks another collection to share with this
 * one. The source is any collection they read of their OWN; its managers
 * decide, and set the two switches, when they answer.
 */
export function RequestLinkModal({ open, onClose, onDone, targetId, candidates = [] }) {
	const [sourceId, setSourceId] = useState(null);
	const [shape, setShape] = useState(WHOLE);
	const [note, setNote] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const vocabulary = useLinkVocabulary(sourceId);

	useEffect(() => {
		if (!open) return;
		setSourceId(null);
		setShape(WHOLE);
		setNote('');
		setError(null);
	}, [open]);

	const submit = async () => {
		if (!sourceId || !shapeComplete(shape)) return;
		setBusy(true);
		setError(null);
		try {
			const request = await requestLink(targetId, { sourceId, ...shapeToPayload(shape), note });
			onDone?.(request);
		} catch (err) {
			setError(describeLinkError(err, 'The request could not be sent.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Request a link"
			okText="Send request"
			okButtonProps={{ loading: busy, disabled: !sourceId || !shapeComplete(shape) }}
			onOk={submit}
			onCancel={onClose}
			destroyOnClose
			className="linked-collections__modal"
		>
			<p className="linked-collections__lead">
				Ask another collection to let everyone who can see the whole of this one read its records, read-only. Nothing is shared until one of its managers approves.
			</p>
			<Field label="Read from">
				<CollectionPicker
					ariaLabel="Read from"
					collections={candidates}
					value={sourceId}
					onChange={next => { setSourceId(next); setShape(WHOLE); }}
					placeholder={candidates.length ? 'Pick a collection you can see' : 'There is no other collection you can see that can be linked'}
				/>
			</Field>
			{sourceId ? (
				<Field label="What to ask for">
					<LinkShapePicker
						value={shape}
						onChange={setShape}
						cardOptions={vocabulary.cardOptions}
						filterFields={vocabulary.filterFields}
						collectionId={sourceId}
					/>
				</Field>
			) : null}
			<Field label="Note (optional)" htmlFor="link-request-note" hint={`Its managers see this in keepr; it is never emailed. Up to ${LINK_LIMITS.noteMax} characters.`}>
				<Input.TextArea
					id="link-request-note"
					rows={3}
					maxLength={LINK_LIMITS.noteMax}
					showCount
					value={note}
					onChange={event => setNote(event.target.value)}
					placeholder="Why this collection needs to read it"
				/>
			</Field>
			<Text type="secondary" className="small">
				Its managers decide, when they approve, whether people here may keep copies and whether this collection's API keys and assistants may read it.
			</Text>
			{error ? <Alert type="error" showIcon role="alert" message={error} /> : null}
		</Modal>
	);
}

/**
 * A's manager answers a request. The approval may NARROW what was asked —
 * a whole request to a card or a filter, a card request to that card or one
 * of its child cards — never widen it; a filter request is approved as asked.
 * `requesterReach` says what the person who asked already sees here.
 */
export function ApproveRequestModal({ request, onClose, onDone, sourceId, vocabulary, cardsById = {} }) {
	const asked = linkShape(request);
	const askedDraft = useMemo(() => (
		asked.kind === 'card' ? { kind: 'card', cardId: asked.cardId, query: null }
			: asked.kind === 'query' ? { kind: 'query', cardId: null, query: asked.query }
				: WHOLE
	), [asked.kind, asked.cardId, asked.query]);
	const [shape, setShape] = useState(askedDraft);
	const [allowCopies, setAllowCopies] = useState(false);
	const [allowKeys, setAllowKeys] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const open = Boolean(request);
	// An approval that writes a new filter ("Filtered records" for a whole
	// request) is resolved like any link's (keepr-api approvedShape →
	// resolveLinkQuery): an element it could mean several of is refused
	// `ambiguous_element`, and answered here, then approved again with the
	// pick. A pick answers the shape it was made for, never a later one.
	const elementPicks = useElementChoices(open ? JSON.stringify(shape) : null);
	const resetElementPicks = elementPicks.reset;

	useEffect(() => {
		if (!open) return;
		setShape(askedDraft);
		setAllowCopies(false);
		setAllowKeys(true);
		setError(null);
		resetElementPicks();
	}, [open, askedDraft, resetElementPicks]);

	const cardMeta = id => vocabulary?.cardMetaById?.get(normalizeId(id)) || cardsById[normalizeId(id)] || null;
	const reach = describeRequesterReach(request?.requesterReach, {
		cardsById,
		targetName: request?.target?.name || 'their collection',
	});
	const exceeds = requestExceedsReach(request, request?.requesterReach);
	const kinds = asked.kind === 'whole' ? ['whole', 'card', 'query'] : [asked.kind];
	const cardIds = asked.kind === 'card'
		? cardAndDescendantIds(Object.values(cardsById || {}), asked.cardId)
		: null;
	const changed = shape.kind !== asked.kind
		|| (shape.kind === 'card' && normalizeId(shape.cardId) !== asked.cardId);

	const submit = async () => {
		if (!shapeComplete(shape) || !reach.approvable) return;
		setBusy(true);
		setError(null);
		try {
			const result = await approveLinkRequest(sourceId, normalizeId(request?._id), elementPicks.withChoices({
				shape: changed ? shapeToPayload(shape) : null,
				allowCopies,
				allowKeys,
			}));
			onDone?.(result);
		} catch (err) {
			if (!elementPicks.take(err)) setError(describeLinkError(err, 'The request could not be approved.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Approve link request"
			okText="Approve"
			okButtonProps={{ loading: busy, disabled: !shapeComplete(shape) || !reach.approvable }}
			onOk={submit}
			onCancel={onClose}
			destroyOnClose
			className="linked-collections__modal"
		>
			<div className="modal-identity">
				<RecordIdentity
					kicker="Asking collection"
					badge={<RecordBadge kind="collection" record={request?.target || null} title={request?.target?.name || 'A collection'} size="md" />}
					below={(
						<span className="linked-collections__asked">
							<span>Asked for</span>
							<LinkShapeSummary row={request} cardMeta={cardMeta} filterFields={vocabulary?.filterFields || []} />
						</span>
					)}
				/>
			</div>
			{request?.requestedBy ? (
				<div className="linked-collections__requester">
					<Text type="secondary">Asked by</Text>
					<UserRef userId={request.requestedBy} size={20} />
				</div>
			) : null}
			{reach.sentence ? (
				<Alert
					type={reach.approvable ? 'info' : 'warning'}
					showIcon
					message={reach.sentence}
					description={reach.approvable && exceeds ? 'Approving shares more than they can see here themselves.' : null}
				/>
			) : null}
			{request?.note ? (
				<blockquote className="linked-collections__note">{request.note}</blockquote>
			) : null}
			<Field
				label="Share"
				hint={asked.kind === 'query'
					? 'A request for filtered records is approved as asked, or declined.'
					: 'You can share less than was asked for, never more.'}
			>
				{asked.kind === 'query' ? (
					<LinkShapeSummary row={request} cardMeta={cardMeta} filterFields={vocabulary?.filterFields || []} />
				) : (
					<LinkShapePicker
						value={shape}
						onChange={setShape}
						kinds={kinds}
						cardIds={cardIds}
						cardOptions={vocabulary?.cardOptions || []}
						filterFields={vocabulary?.filterFields || []}
						collectionId={sourceId}
					/>
				)}
			</Field>
			<Switches allowCopies={allowCopies} allowKeys={allowKeys} onCopies={setAllowCopies} onKeys={setAllowKeys} />
			{error ? <Alert type="error" showIcon role="alert" message={error} /> : null}
			<QueryRefusalPick
				inline
				refusal={elementPicks.refusal}
				disabled={busy}
				onPick={(value) => { elementPicks.pick(value); submit(); }}
				onRepick={() => { elementPicks.clearRefusal(); submit(); }}
				access={shape.kind === 'query' ? (shape.query || '') : (asked.query || '')}
			/>
		</Modal>
	);
}

/** A's manager says no — the note reaches the asking collection's managers in keepr, never by email. */
export function DeclineRequestModal({ request, onClose, onDone, sourceId }) {
	const [note, setNote] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const open = Boolean(request);

	useEffect(() => {
		if (!open) return;
		setNote('');
		setError(null);
	}, [open]);

	const submit = async () => {
		setBusy(true);
		setError(null);
		try {
			const result = await declineLinkRequest(sourceId, normalizeId(request?._id), { note });
			onDone?.(result);
		} catch (err) {
			setError(describeLinkError(err, 'The request could not be declined.'));
		} finally {
			setBusy(false);
		}
	};

	const name = request?.target?.name || 'the asking collection';
	return (
		<Modal
			open={open}
			title="Decline link request"
			okText="Decline"
			okButtonProps={{ danger: true, loading: busy }}
			onOk={submit}
			onCancel={onClose}
			destroyOnClose
			className="linked-collections__modal"
		>
			<p>{`${name}'s managers are told. They can ask again after a day.`}</p>
			<Field label="Note (optional)" htmlFor="link-decline-note" hint="They see this in keepr; it is never emailed.">
				<Input.TextArea
					id="link-decline-note"
					rows={3}
					maxLength={LINK_LIMITS.noteMax}
					showCount
					value={note}
					onChange={event => setNote(event.target.value)}
				/>
			</Field>
			{error ? <Alert type="error" showIcon role="alert" message={error} /> : null}
		</Modal>
	);
}
